// Short-lived, in-memory cache + in-flight dedup for integration connection
// checks (Shopify / WooCommerce / Razorpay / Cashfree / PayU / Custom API).
//
// Why: `checkConnection` for each provider is a raw fetch fired on mount from
// many independent places — Integrations.tsx runs a Promise.all over EVERY
// provider, NavigationCard.tsx does the same, and each provider's own page
// checks again. Navigating Dashboard → Integrations → a provider page therefore
// re-checks every provider several times over. None of it was cached, so one
// HAR showed ~10 calls per provider.
//
// This wraps each provider's `checkConnection` so that:
//   1. concurrent callers (the Promise.all bursts) share ONE in-flight request,
//   2. the result is reused for a short TTL, collapsing the navigation re-checks,
//   3. connect/disconnect mutations clear the entry so state never goes stale
//      after a user action.
//
// The TTL is deliberately short (30s) so connection state is never stale for
// long even without an explicit clear (e.g. an OAuth redirect completed on the
// backend) — a manual "Refresh" a bit later always re-hits the network.

const TTL_MS = 30_000;

interface CacheEntry {
  at: number;
  promise: Promise<any>;
}

const cache = new Map<string, CacheEntry>();

/**
 * Run `fetcher` behind a keyed TTL cache. Concurrent calls for the same key
 * share the in-flight promise; a settled result is reused until TTL expires.
 * A rejected fetch is evicted immediately so the next call retries.
 */
export async function cachedConnectionCheck<T>(
  key: string,
  fetcher: () => Promise<T>
): Promise<T> {
  const now = Date.now();
  const hit = cache.get(key);
  if (hit && now - hit.at <= TTL_MS) {
    return hit.promise as Promise<T>;
  }

  const promise = (async () => {
    try {
      return await fetcher();
    } catch (err) {
      // Don't cache failures — evict so the next call re-attempts.
      const current = cache.get(key);
      if (current && current.promise === promise) cache.delete(key);
      throw err;
    }
  })();

  cache.set(key, { at: now, promise });
  return promise;
}

/**
 * Invalidate cached connection checks. Pass a key prefix (e.g. "shopify:") to
 * clear one provider, or omit to clear everything. Called by connect/disconnect
 * so a state change is reflected on the very next check.
 */
export function clearConnectionCheckCache(keyPrefix?: string): void {
  if (!keyPrefix) {
    cache.clear();
    return;
  }
  for (const k of Array.from(cache.keys())) {
    if (k.startsWith(keyPrefix)) cache.delete(k);
  }
}
